/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';

import {
  Main,
  ContentGroup,
  Paragraph,
  Accordion,
  Heading,
  List,
  ListItem,
  Hr,
  GridItem,
  Grid,
} from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import { StyledLink } from 'client/components/styledLink/StyledLink';
import * as routes from 'client/routes/manifest';

import { MortgagePaymentsRecalculationContent } from './MortgagePaymentsRecalculationPage.config';

function MortgagePaymentsRecalculationPage(): React.JSX.Element {
  const {
    ifYouPrefer,
    heading1,
    backButton,
    usefulGuide,
    keepOnTrack,
    youCanWatch,
    thingsToKnow,
    frequentQuestions,
    videoLink,
  } = useContent<MortgagePaymentsRecalculationContent>();

  return (
    <Main>
      <ContentGroup marginTop="05">
        <StyledLink
          iconPosition="left"
          data-test-id="back-button-link"
          to={routes.KnowledgeBase}
        >
          {backButton}
        </StyledLink>

        <ContentGroup marginTop="05">
          <Heading as="h6" size="s6">
            {heading1}
          </Heading>
        </ContentGroup>

        <Heading as="h5" size="s5">
          {usefulGuide}
        </Heading>
        <Hr />
        <Heading>{keepOnTrack}</Heading>
        <Paragraph>{youCanWatch.paragraph}</Paragraph>

        <Paragraph>{ifYouPrefer.paragraph1}</Paragraph>
        <List>
          <ListItem>{ifYouPrefer.bulletPoint1}</ListItem>
          <ListItem>{ifYouPrefer.bulletPoint2}</ListItem>
          <ListItem>{ifYouPrefer.bulletPoint3}</ListItem>
        </List>
        <Grid>
          <GridItem sm={12} md={12} key={videoLink.id}>
            <iframe
              src={`https://www.youtube.com/embed/${videoLink.id}`}
              title={videoLink.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="mortgage-Payments-Recalculation-youtube-iframe"
              style={{
                minWidth: '100%',
                minHeight: '700px',
              }}
            />
          </GridItem>
        </Grid>

        <Hr />
        <Heading>{thingsToKnow.header1}</Heading>

        <Heading as="h3" size="s3">
          {thingsToKnow.interestOnly.interestHeader}
        </Heading>
        <Paragraph>{thingsToKnow.interestOnly.paragraph1}</Paragraph>
        <Paragraph>{thingsToKnow.interestOnly.paragraph2}</Paragraph>
        <Paragraph>{thingsToKnow.interestOnly.paragraph3}</Paragraph>
        <Heading as="h3" size="s3">
          {thingsToKnow.continuePreviousPayments.paymentHeader}
        </Heading>
        <Paragraph>
          {thingsToKnow.continuePreviousPayments.paragraph1}
        </Paragraph>
        <Paragraph marginBottom="none">
          {thingsToKnow.continuePreviousPayments.erc}
        </Paragraph>
        <Paragraph>
          {thingsToKnow.continuePreviousPayments.paragraph2}
        </Paragraph>
        <Paragraph>
          {thingsToKnow.continuePreviousPayments.paragraph3}
        </Paragraph>

        <ContentGroup marginTop="01">
          <StyledLink to={routes.MakingOverpayments}>
            {thingsToKnow.continuePreviousPayments.furtherInformation.text}
          </StyledLink>
        </ContentGroup>

        <Heading as="h3" size="s3" marginTop="03" marginBottom="02">
          {thingsToKnow.missedPayment.missedHeader}
        </Heading>
        <Paragraph>{thingsToKnow.missedPayment.paragraph1}</Paragraph>
        <Paragraph>{thingsToKnow.missedPayment.paragraph2}</Paragraph>

        <ContentGroup marginTop="01">
          <StyledLink to={routes.WorriedAboutFuturePayments}>
            {thingsToKnow.missedPayment.futurePayments.text}
          </StyledLink>
        </ContentGroup>

        <Heading as="h3" size="s3">
          {thingsToKnow.reduceMortgage.reduceHeader}
        </Heading>
        <Paragraph>{thingsToKnow.reduceMortgage.paragraph1}</Paragraph>

        <Hr />
        <Heading as="h5" size="s5">
          {frequentQuestions.frequentlyAsked}
        </Heading>
        <Hr />

        <Accordion
          label={frequentQuestions.updatePayments.paymentQuery}
          variation="default"
        >
          <ContentGroup marginTop="05" marginBottom="none">
            <Paragraph>
              {frequentQuestions.updatePayments.accordion.paragraph1}
            </Paragraph>
          </ContentGroup>
        </Accordion>
        <Hr marginBottom="04" marginTop="04" />

        <Accordion
          label={frequentQuestions.smallPaymentChange.smallQuery}
          variation="default"
        >
          <ContentGroup marginTop="05" marginBottom="none">
            <Paragraph>
              {frequentQuestions.smallPaymentChange.accordion.paragraph1}
            </Paragraph>
            <Paragraph>
              {frequentQuestions.smallPaymentChange.accordion.paragraph2}
            </Paragraph>
          </ContentGroup>
        </Accordion>
        <Hr marginBottom="04" marginTop="04" />

        <Accordion
          label={frequentQuestions.fixedInterestChange.interestQuery}
          variation="default"
        >
          <ContentGroup marginTop="05" marginBottom="none">
            <Paragraph>
              {frequentQuestions.fixedInterestChange.accordion.paragraph1}
            </Paragraph>
          </ContentGroup>
        </Accordion>
        <Hr marginBottom="04" marginTop="04" />

        <Accordion
          label={frequentQuestions.paymentChangeReplacement.changeQuery}
          variation="default"
        >
          <ContentGroup marginTop="05" marginBottom="none">
            <Paragraph>
              {frequentQuestions.paymentChangeReplacement.accordion.paragraph1}
            </Paragraph>
          </ContentGroup>
        </Accordion>
        <Hr marginBottom="04" marginTop="04" />

        <Accordion
          label={frequentQuestions.paymentChange.multipleQuery}
          variation="default"
        >
          <ContentGroup marginTop="05" marginBottom="none">
            <Heading as="h2" size="s2">
              {frequentQuestions.paymentChange.accordion.heading1}
            </Heading>
            <Paragraph>
              {frequentQuestions.paymentChange.accordion.paragraph1}
            </Paragraph>
            <Heading as="h2" size="s2">
              {frequentQuestions.paymentChange.accordion.heading2}
            </Heading>
            <Paragraph>
              {frequentQuestions.paymentChange.accordion.paragraph2}
            </Paragraph>
            <Heading as="h2" size="s2">
              {frequentQuestions.paymentChange.accordion.heading3}
            </Heading>
            <Paragraph>
              {frequentQuestions.paymentChange.accordion.paragraph3}
            </Paragraph>
          </ContentGroup>
        </Accordion>
        <Hr marginBottom="04" marginTop="04" />

        <Accordion
          label={frequentQuestions.calculateInterest.calculateQuery}
          variation="default"
        >
          <ContentGroup marginTop="05" marginBottom="none">
            <Paragraph>
              {frequentQuestions.calculateInterest.accordion.paragraph1}
            </Paragraph>
            <Paragraph>
              {frequentQuestions.calculateInterest.accordion.paragraph2}
            </Paragraph>
          </ContentGroup>
        </Accordion>
        <Hr marginBottom="04" marginTop="04" />
      </ContentGroup>
    </Main>
  );
}

export default MortgagePaymentsRecalculationPage;
